import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 数学公式动画 - 二次方程求解
 * 
 * 核心效果：
 * 1. 公式逐步显现
 * 2. 参数动态变化
 * 3. 解答步骤分解
 * 4. 图形可视化
 */

const BG = '#0a0e27';
const PRIMARY = '#00d9ff';
const SECONDARY = '#ff6b6b';
const ACCENT = '#ffd93d';

export const MathEquationAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  // 公式动画
  const formulaOpacity = interpolate(frame, [40, 70], [0, 1], { extrapolateRight: 'clamp' });
  const formulaY = interpolate(frame, [40, 80], [-50, 0], { extrapolateRight: 'clamp' });

  // 参数动画
  const a = interpolate(frame, [80, 120], [0, 1], { extrapolateRight: 'clamp' });
  const b = interpolate(frame, [100, 140], [0, -5], { extrapolateRight: 'clamp' });
  const c = interpolate(frame, [120, 160], [0, 6], { extrapolateRight: 'clamp' });

  // 步骤动画
  const step1Opacity = interpolate(frame, [180, 210], [0, 1], { extrapolateRight: 'clamp' });
  const step2Opacity = interpolate(frame, [240, 270], [0, 1], { extrapolateRight: 'clamp' });
  const step3Opacity = interpolate(frame, [300, 330], [0, 1], { extrapolateRight: 'clamp' });

  // 结果动画
  const resultOpacity = interpolate(frame, [360, 390], [0, 1], { extrapolateRight: 'clamp' });
  const resultScale = spring({
    frame: frame - 360,
    from: 0.5,
    to: 1,
    fps,
    config: { damping: 10, stiffness: 80 },
  });

  // 图形动画
  const graphOpacity = interpolate(frame, [420, 460], [0, 1], { extrapolateRight: 'clamp' });
  const graphDrawProgress = interpolate(frame, [460, 560], [0, 1], { extrapolateRight: 'clamp' });

  // 计算结果
  const discriminant = b * b - 4 * a * c;
  const x1 = (-b + Math.sqrt(Math.max(0, discriminant))) / (2 * a);
  const x2 = (-b - Math.sqrt(Math.max(0, discriminant))) / (2 * a);

  return (
    <AbsoluteFill style={{
      background: BG,
      fontFamily: 'Inter, system-ui, sans-serif',
      color: '#e5e7eb',
      padding: 60,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* 标题 */}
      <div style={{
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `scale(${titleScale})`,
      }}>
        <h1 style={{
          fontSize: 72,
          fontWeight: 800,
          margin: 0,
          background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: 2,
        }}>
          二次方程求解
        </h1>
        <p style={{
          fontSize: 28,
          color: '#94a3b8',
          marginTop: 12,
        }}>
          Quadratic Equation • ax² + bx + c = 0
        </p>
      </div>

      {/* 主内容区 */}
      <div style={{
        display: 'flex',
        gap: 60,
        marginTop: 40,
        flex: 1,
      }}>
        {/* 左侧：公式和步骤 */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 30,
        }}>
          {/* 求根公式 */}
          <div style={{
            background: 'rgba(0, 217, 255, 0.08)',
            borderRadius: 20,
            padding: 40,
            border: `2px solid ${PRIMARY}`,
            opacity: formulaOpacity,
            transform: `translateY(${formulaY}px)`,
          }}>
            <div style={{
              fontSize: 24,
              color: PRIMARY,
              marginBottom: 20,
              fontWeight: 600,
            }}>
              📐 求根公式
            </div>
            <div style={{
              fontSize: 56,
              fontWeight: 700,
              color: '#ffffff',
              textAlign: 'center',
              fontFamily: 'Georgia, serif',
            }}>
              x = (-b ± √(b²-4ac)) / 2a
            </div>
          </div>

          {/* 方程参数 */}
          <div style={{
            background: 'rgba(255, 107, 107, 0.08)',
            borderRadius: 20,
            padding: 30,
            border: `2px solid ${SECONDARY}`,
            opacity: interpolate(frame, [80, 110], [0, 1], { extrapolateRight: 'clamp' }),
          }}>
            <div style={{
              fontSize: 24,
              color: SECONDARY,
              marginBottom: 20,
              fontWeight: 600,
            }}>
              🎯 方程参数
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              fontSize: 48,
              fontWeight: 700,
            }}>
              <div style={{ color: PRIMARY }}>
                a = {a.toFixed(0)}
              </div>
              <div style={{ color: SECONDARY }}>
                b = {b.toFixed(0)}
              </div>
              <div style={{ color: ACCENT }}>
                c = {c.toFixed(0)}
              </div>
            </div>
          </div>

          {/* 求解步骤 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
          }}>
            {/* Step 1 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 15,
              padding: '20px 30px',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              opacity: step1Opacity,
            }}>
              <span style={{ color: PRIMARY, fontSize: 28, fontWeight: 700 }}>
                ① 计算判别式:
              </span>
              <span style={{ marginLeft: 20, fontSize: 28 }}>
                Δ = b² - 4ac = {b.toFixed(0)}² - 4×{a.toFixed(0)}×{c.toFixed(0)} = {discriminant.toFixed(0)}
              </span>
            </div>

            {/* Step 2 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 15,
              padding: '20px 30px',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              opacity: step2Opacity,
            }}>
              <span style={{ color: PRIMARY, fontSize: 28, fontWeight: 700 }}>
                ② 代入公式:
              </span>
              <span style={{ marginLeft: 20, fontSize: 28 }}>
                x = (-{b.toFixed(0)} ± √{discriminant.toFixed(0)}) / {2 * a.toFixed(0)}
              </span>
            </div>

            {/* Step 3 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 15,
              padding: '20px 30px',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              opacity: step3Opacity,
            }}>
              <span style={{ color: PRIMARY, fontSize: 28, fontWeight: 700 }}>
                ③ 计算结果:
              </span>
              <span style={{ marginLeft: 20, fontSize: 28 }}>
                x₁ = {x1.toFixed(1)}, x₂ = {x2.toFixed(1)}
              </span>
            </div>
          </div>
        </div>

        {/* 右侧：结果和图形 */}
        <div style={{
          width: 520,
          display: 'flex',
          flexDirection: 'column',
          gap: 30,
        }}>
          {/* 结果卡片 */}
          <div style={{
            background: `linear-gradient(135deg, rgba(0, 217, 255, 0.15), rgba(255, 107, 107, 0.15))`,
            borderRadius: 20,
            padding: 40,
            border: `3px solid ${PRIMARY}`,
            opacity: resultOpacity,
            transform: `scale(${resultScale})`,
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 28,
              color: '#94a3b8',
              marginBottom: 20,
            }}>
              ✨ 方程的解
            </div>
            <div style={{
              fontSize: 64,
              fontWeight: 800,
              color: '#ffffff',
              marginBottom: 10,
            }}>
              x₁ = {x1.toFixed(2)}
            </div>
            <div style={{
              fontSize: 64,
              fontWeight: 800,
              color: '#ffffff',
            }}>
              x₂ = {x2.toFixed(2)}
            </div>
            <div style={{
              marginTop: 20,
              padding: '15px 25px',
              background: 'rgba(255, 217, 61, 0.2)',
              borderRadius: 12,
              fontSize: 24,
              color: ACCENT,
              fontWeight: 600,
            }}>
              Δ {'>'} 0 → 两个不相等实根
            </div>
          </div>

          {/* 图形可视化 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 20,
            padding: 30,
            border: '2px solid rgba(255, 255, 255, 0.1)',
            opacity: graphOpacity,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              width: 400,
              height: 300,
              position: 'relative',
            }}>
              {/* X轴 */}
              <div style={{
                position: 'absolute',
                bottom: 150,
                left: 0,
                right: 0,
                height: 2,
                background: '#4a5568',
              }} />
              {/* Y轴 */}
              <div style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 200,
                width: 2,
                background: '#4a5568',
              }} />
              {/* 抛物线（简化版） */}
              <svg
                width="400"
                height="300"
                style={{ position: 'absolute', top: 0, left: 0 }}
              >
                <path
                  d={`M 0,${300 - graphDrawProgress * 250} Q 200,${300 - graphDrawProgress * 50} 400,${300 - graphDrawProgress * 250}`}
                  stroke={PRIMARY}
                  strokeWidth={4}
                  fill="none"
                  strokeLinecap="round"
                />
                {/* 根的位置 */}
                {graphDrawProgress > 0.8 && (
                  <>
                    <circle cx={100} cy={150} r={10} fill={SECONDARY} />
                    <circle cx={300} cy={150} r={10} fill={SECONDARY} />
                    <text x={100} y={180} textAnchor="middle" fill="#fff" fontSize={20}>
                      x₁
                    </text>
                    <text x={300} y={180} textAnchor="middle" fill="#fff" fontSize={20}>
                      x₂
                    </text>
                  </>
                )}
              </svg>
              {/* 标签 */}
              <div style={{
                position: 'absolute',
                bottom: 5,
                right: 10,
                fontSize: 18,
                color: '#94a3b8',
              }}>
                y = {a.toFixed(0)}x² + ({b.toFixed(0)})x + {c.toFixed(0)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
